import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { api } from "./client";

// ── Brand profile (caller's tenant's first one) ─────────────────────
export type MyBrandProfile = {
  id: string;
  tenantId: string;
  name: string;
  accent: string;
  secondary: string;
  tagline: string;
  rhText: string | null;
  activationUrl: string | null;
  logoText: string | null;
  logoUrl: string | null;
  locked: boolean;
};

export function useMyBrandProfile() {
  return useQuery({
    queryKey: ["my-brand-profile"],
    queryFn: async () => {
      const { profiles } = await api<{ profiles: MyBrandProfile[] }>("/api/v1/brand-profiles");
      return profiles[0] ?? null;
    },
  });
}

// ── Single order fetch (used by the Order Confirmation screen) ─────
export type OrderDetail = {
  id: string;
  tenantId: string;
  tenantName: string;
  kitCode: string;
  kitTitle: string;
  qty: number;
  unitPricePence: number;
  totalPence: number;
  status: string;
  placedAt: string;
  holdReason: string | null;
  royalMailTracking: string | null;
  factoryReceivedAt: string | null;
  packedAt: string | null;
  dispatchedAt: string | null;
  brandProfileName: string;
  brandProfileTagline: string;
};

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: ["order", id],
    queryFn: async () => {
      const { order } = await api<{ order: OrderDetail }>(`/api/v1/orders/${id}`);
      return order;
    },
    enabled: Boolean(id),
  });
}

// ── usePlaceOrder: creates a design from the in-memory brand state,
//    snapshots it as version 1, then places an order. Returns the
//    server-generated order id (STD-YYYY-NNNNN) for the UI to navigate to.
type PlaceOrderArgs = {
  kitId: string;
  brandProfileId: string;
  designName: string;
  qty: number;
  snapshot: {
    brand: {
      accent: string;
      secondary: string;
      tagline: string;
      rhText: string | null;
      activationUrl: string | null;
      logoText: string | null;
      logoGcsPath: string | null;
    };
    content: { quantity: number; orgName: string };
  };
};

export function usePlaceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: PlaceOrderArgs) => {
      // 1. Create the design
      const { design } = await api<{ design: { id: string; currentVersion: number } }>(
        "/api/v1/designs",
        {
          method: "POST",
          body: { kitId: args.kitId, brandProfileId: args.brandProfileId, name: args.designName },
        },
      );

      // 2. Save the user's brand-state snapshot as version 2 (initial
      //    placeholder is version 1; this captures the latest edits)
      await api(`/api/v1/designs/${design.id}/versions`, {
        method: "POST",
        body: { snapshot: args.snapshot, note: "Snapshot at order placement" },
      });

      // 3. Place the order
      const { order } = await api<{
        order: { id: string; status: string; totalPence: number; holdReason: string | null };
      }>("/api/v1/orders", {
        method: "POST",
        body: { designId: design.id, qty: args.qty },
      });

      return order;
    },
    onSuccess: () => {
      // Invalidate everything that could have changed
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["designs"] });
      qc.invalidateQueries({ queryKey: ["admin", "orders"] });
      qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}
